import Emoji, { EmojiName } from '@components/common/data-display/emoji';
import { AspectRatio, Button, Card, Grid, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import _ from 'lodash';
import Image from 'next/image';
import React, { MouseEventHandler } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import Price from '../../../../../components/common/data-display/price';
import cartSlice from '../../../../../reducers/cart';
import { AppDispatch } from '../../../../../stores';
import { safeString } from '../../../../../utils/stringUtil';
import { MenuItemCardProps, MenuItemWithCategories } from '../../types';
import MenuOrderItem from './components/menuOrderItem';

type Props = MenuItemCardProps & {
  onClick?: MouseEventHandler | undefined;
  setMenuItemDetail: (e: MenuItemWithCategories) => void;
  setShowToppingModal: (e: boolean) => void;
  open: () => void;
};

// eslint-disable-next-line react/display-name
const MenuItemCard: React.FC<Props> = React.memo(
  ({ menuItem, setMenuItemDetail, setShowToppingModal, open }) => {
    const { title, price, discount, imageUrl, description, available, toppingCategories, tags, id } = menuItem;

    const theme = useMantineTheme();

    const dispatch: AppDispatch = useDispatch();

    const isItemAvailable = available;
    const addButtonText = isItemAvailable ? 'افزودن' : 'تمام شد';

    const onAddToCardClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (toppingCategories?.length) {
        setMenuItemDetail(menuItem);
        setShowToppingModal(true);
      } else {
        dispatch(
          cartSlice.actions.createOrder({
            ...menuItem,
            count: 1,
            temporaryOrderId: uuidV4(),
            orderItemToppings: [],
          })
        );
      }
    };

    return (
      <Card
        padding="xs"
        radius="md"
        onClick={() => {
          open();
          setMenuItemDetail(menuItem);
        }}
      >
        <Stack>
          <Grid justify="space-between">
            <Grid.Col span="auto">
              <Stack spacing="xs">
                <Stack>
                  <Group spacing={0}>
                    {tags?.map((emoji) => <Emoji key={`tag-${emoji}`} name={emoji as EmojiName} />)}
                  </Group>

                  <Title order={4} fz={14} weight="bold">
                    {title}
                  </Title>
                </Stack>

                <Text size="xs" color="brand.2">
                  {description}
                </Text>
                <Price price={price || 0} discount={discount} />
              </Stack>
            </Grid.Col>

            <Grid.Col span="content">
              <Stack spacing="xs">
                <AspectRatio miw={84} ratio={1 / 1} mx="auto">
                  <Image
                    alt={safeString(title)}
                    width={84}
                    height={84}
                    src={imageUrl}
                    style={{ borderRadius: theme.radius.md }}
                  />
                </AspectRatio>

                <Button
                  id="add_to_basket_main"
                  variant="outline"
                  size="sm"
                  disabled={!isItemAvailable}
                  onClick={onAddToCardClick}
                >
                  {addButtonText}
                </Button>
              </Stack>
            </Grid.Col>
          </Grid>

          <MenuOrderItem orderItemId={safeString(id)} />
        </Stack>
      </Card>
    );
  },
  (prevProps, nextProps) => _.isEqual(prevProps.menuItem, nextProps.menuItem)
);

export default MenuItemCard;
